import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { SearchCheck, BadgePercent, RefreshCw, ArrowRight } from "lucide-react"
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import ReviewsDetails from "../components/ReviewsDetails"

const Home = () => {
  const [featuredReviews, setFeaturedReviews] = useState([])

  useEffect(() => {
    const fetchFeaturedReviews = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/bonuses/featured`)
        const data = await res.json()
        setFeaturedReviews(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error("Failed to fetch featured bonuses:", err)
      }
    }

    fetchFeaturedReviews()
  }, [])

  const featuredSlots = useMemo(() => {
    const bySlot = new Map()

    featuredReviews.forEach((item) => {
      const slot = Number(item.featuredHomeSlot)
      if (slot >= 1 && slot <= 12 && !bySlot.has(slot)) {
        bySlot.set(slot, item)
      }
    })

    return Array.from({ length: 12 }, (_, index) => ({
      slot: index + 1,
      item: bySlot.get(index + 1),
    }))
  }, [featuredReviews])

  return (
    <div className="container mx-auto px-4 py-10 space-y-16">
      {/* Hero */}
      <section className="py-4 md:py-10 text-center relative">
        <h1 className="text-4xl md:text-7xl font-bold mb-6 font-display bg-gradient-to-r from-[#FF6B00] via-[#FF9A3D] to-[#FFD700] bg-clip-text text-transparent drop-shadow-glow">
          CS2 & Rust Case Statistics
        </h1>
        <p className="text-blue-100/80 text-lg max-w-2xl mx-auto">
          Compare RTP across different case opening websites and make informed decisions
        </p>
        <div className="flex justify-center gap-4 mt-6 flex-wrap">
          <Link to="/websites">
            <Button size="lg" variant="outline" className="!bg-[#1A1D21]/40 !border-[#FF6B00] !text-[#FF6B00] hover:opacity-90 rounded-xl backdrop-blur-sm">
              Browse Websites
            </Button>
          </Link>
          <Link to="/bonuses">
            <Button size="lg" className="bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] hover:opacity-90 text-white rounded-xl shadow-glow-sm">
              Claim Bonuses
            </Button>
          </Link>
        </div>
        <div className="flex justify-center mt-4">
          <Link to="/best-cases">
            <Button size="lg" variant="outline" className="!bg-[#1A1D21]/40 !border-[#FF6B00] !text-[#FF6B00] hover:opacity-90 rounded-xl backdrop-blur-sm">
              Best Cases
            </Button>
          </Link>
        </div>

      </section>

      {featuredReviews.length > 0 && (
        <section className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-3xl font-display text-white">Featured Websites</h2>
            <Link to="/bonuses" className="text-[#FF6B00] hover:underline flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {featuredSlots.map(({ slot, item }) => (
              item ? (
                <ReviewsDetails
                  key={item._id || `${item.website}-${slot}`}
                  website={item.website}
                  affiliateLink={item.affiliateLink}
                  review={item.review}
                  logo={item.logo}
                  code={item.code}
                  games={item.games}
                  rtp={item.rtp}
                />
              ) : (
                <div key={`featured-slot-${slot}`} className="hidden xl:block" aria-hidden="true" />
              )
            ))}
          </div>
        </section>
      )}

      <section className="py-12 relative">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 font-display text-white">
            Why Use <span className="text-[#FF6B00]">CaseRTP</span>?
          </h2>
          <p className="text-blue-100/80 max-w-2xl mx-auto">
            We provide transparent data on case opening websites to help you make better decisions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="!bg-[#1A1D21]/40 border-[#2A2D31] backdrop-blur-sm hover:shadow-[0_8px_30px_rgba(255,107,0,0.12)] transition-all duration-300 overflow-hidden group pt-4">
            <div className="absolute inset-0 !bg-gradient-to-br from-[#FF6B00]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <CardContent className="p-8 relative">
              <div className="w-16 h-16 rounded-2xl !bg-[#FF6B00]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <SearchCheck className="h-8 w-8 text-[#FF6B00]" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-display text-white">Find Better Value Cases</h3>
              <p className="text-blue-100/70">
                We track RTP data to help you compare case opening websites to find the best value cases to open.
              </p>
            </CardContent>
          </Card>

          <Card className="!bg-[#1A1D21]/40 border-[#2A2D31] backdrop-blur-sm hover:shadow-[0_8px_30px_rgba(255,107,0,0.12)] transition-all duration-300 overflow-hidden group pt-4">
            <div className="absolute inset-0 !bg-gradient-to-br from-[#FF6B00]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <CardContent className="p-8 relative">
              <div className="w-16 h-16 rounded-2xl !bg-[#FF6B00]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <BadgePercent className="h-8 w-8 text-[#FF6B00]" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-display text-white">Best Bonuses & Promo Codes</h3>
              <p className="text-blue-100/70">
                Find the latest bonus offers, promo codes, deposit rewards and free cases from popular case opening sites.
              </p>
            </CardContent>
          </Card>

          <Card className="!bg-[#1A1D21]/40 border-[#2A2D31] backdrop-blur-sm hover:shadow-[0_8px_30px_rgba(255,107,0,0.12)] transition-all duration-300 overflow-hidden group pt-4">
            <div className="absolute inset-0 !bg-gradient-to-br from-[#FF6B00]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <CardContent className="p-8 relative">
              <div className="w-16 h-16 rounded-2xl !bg-[#FF6B00]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <RefreshCw className="h-8 w-8 text-[#FF6B00]" />
              </div>
              <h3 className="text-xl font-bold mb-2 font-display text-white">Check Before Opening</h3>
              <p className="text-blue-100/70">
                We strive to keep RTP data updated but odds can change. Always verify the case odds before you open.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  )
}

export default Home
